/**
 * DynamicParticles Visualization Component
 * iPhone Style - Dynamic Speed Particles
 * Canvas-based particles that orbit around center with dynamic speed
 */

import type { FC } from 'react';
import { useEffect, useRef } from 'react';

interface DynamicParticlesProps {
  isActive: boolean;
  size: number;
}

interface Particle {
  baseX: number;
  baseY: number;
  x: number;
  y: number;
  size: number;
  speed: number;
  time: number;
  idleDistance: number;
  activeDistance: number;
  phase: number; // Phase offset to maintain position during transitions
}

export const DynamicParticles: FC<DynamicParticlesProps> = ({ isActive, size }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  const particlesRef = useRef<Particle[]>([]);
  const previousActiveRef = useRef<boolean>(isActive);
  const transitionStartTimeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    ctx.scale(dpr, dpr);

    const centerX = size / 2;
    const centerY = size / 2;

    // Get colors based on theme mode
    const isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches ||
      document.documentElement.classList.contains('dark');
    
    const accentGold = getComputedStyle(document.documentElement)
      .getPropertyValue('--accent-gold')
      .trim() || '#8b6f47';
    
    // Use accent gold in dark mode, darker color in light mode for contrast
    const particleColor = isDarkMode ? accentGold : '#78350F'; // Dark brown for light mode contrast

    // Initialize particles - scale positions to fit within visualization size
    if (particlesRef.current.length === 0) {
      // Use 70% of size for particle spread to ensure visibility
      const maxRadius = size * 0.35;
      const particlePositions = [
        { baseX: centerX - maxRadius * 0.6, baseY: centerY - maxRadius * 0.9 },
        { baseX: centerX + maxRadius * 0.3, baseY: centerY - maxRadius * 0.75 },
        { baseX: centerX - maxRadius * 0.9, baseY: centerY - maxRadius * 0.3 },
        { baseX: centerX + maxRadius * 0.6, baseY: centerY - maxRadius * 0.45 },
        { baseX: centerX - maxRadius * 0.45, baseY: centerY + maxRadius * 0.15 },
        { baseX: centerX + maxRadius * 0.45, baseY: centerY },
        { baseX: centerX - maxRadius * 0.75, baseY: centerY + maxRadius * 0.6 },
        { baseX: centerX + maxRadius * 0.15, baseY: centerY + maxRadius * 0.75 },
        { baseX: centerX - maxRadius * 0.3, baseY: centerY - maxRadius * 1.05 },
        { baseX: centerX + maxRadius * 0.75, baseY: centerY + maxRadius * 0.45 },
      ];

      const baseParticleSize = Math.max(2, size / 25); // Scale particle size proportionally
      particlesRef.current = particlePositions.map((pos) => ({
        baseX: pos.baseX,
        baseY: pos.baseY,
        x: pos.baseX,
        y: pos.baseY,
        size: baseParticleSize + Math.random() * (baseParticleSize * 0.5), // Scaled size
        speed: Math.random() * 0.02 + 0.01,
        time: Math.random() * Math.PI * 2,
        phase: Math.random() * Math.PI * 2, // Initial phase for smooth transitions
        idleDistance: Math.max(2, size / 30), // Scale distance proportionally
        activeDistance: Math.max(6, size / 10), // Scale distance proportionally
      }));
    }

    // Handle smooth transition when active state changes
    if (previousActiveRef.current !== isActive) {
      transitionStartTimeRef.current = Date.now();
      // Adjust phase to maintain visual position during transition
      particlesRef.current.forEach((particle) => {
        // Calculate current angle
        const currentAngle = Math.atan2(
          particle.y - particle.baseY,
          particle.x - particle.baseX
        );
        // Set phase to match current position
        particle.phase = currentAngle;
        // Reset time to maintain phase
        particle.time = 0;
      });
      previousActiveRef.current = isActive;
    }

    // Animation loop
    function animate() {
      if (!ctx) return;
      ctx.clearRect(0, 0, size, size);

      // Smooth transition duration (300ms)
      const transitionDuration = 300;
      const transitionElapsed = Date.now() - transitionStartTimeRef.current;
      const transitionProgress = Math.min(transitionElapsed / transitionDuration, 1);
      // Easing function for smooth transition
      const easeInOut = transitionProgress < 0.5
        ? 2 * transitionProgress * transitionProgress
        : 1 - Math.pow(-2 * transitionProgress + 2, 2) / 2;

      particlesRef.current.forEach((particle) => {
        // Interpolate between idle and active speeds during transition
        const targetSpeed = isActive ? particle.speed * 3 : particle.speed;
        const baseSpeed = previousActiveRef.current ? particle.speed * 3 : particle.speed;
        const currentSpeed = transitionProgress < 1
          ? baseSpeed + (targetSpeed - baseSpeed) * easeInOut
          : targetSpeed;
        
        // Interpolate between idle and active distances during transition
        const targetDistance = isActive ? particle.activeDistance : particle.idleDistance;
        const baseDistance = previousActiveRef.current ? particle.activeDistance : particle.idleDistance;
        const currentDistance = transitionProgress < 1
          ? baseDistance + (targetDistance - baseDistance) * easeInOut
          : targetDistance;

        // Update particle position with smooth transition
        particle.time += currentSpeed;
        const angle = particle.phase + particle.time;
        
        particle.x = particle.baseX + Math.cos(angle) * currentDistance;
        particle.y = particle.baseY + Math.sin(angle) * currentDistance;

        // Draw particle
        if (!ctx) return;
        ctx.save();
        ctx.fillStyle = particleColor;
        ctx.globalAlpha = 0.8; // Consistent opacity, no dimming
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size / 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });

      animationFrameRef.current = requestAnimationFrame(animate);
    }

    animate();

    // Cleanup
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isActive, size]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        display: 'block',
        width: `${size}px`,
        height: `${size}px`,
      }}
      aria-hidden="true"
    />
  );
};

